/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { useState, useLayoutEffect, RefObject } from 'react';

declare global {
  interface Document {
    onwebkitfullscreenchange?: () => void;
    mozFullScreenElement?: Element;
    msFullscreenElement?: Element;
    webkitFullscreenElement?: Element;
    webkitExitFullscreen?: () => void;
  }
  interface HTMLElement {
    webkitRequestFullscreen?: () => Promise<void>;
  }
}

export default function useFullscreenStatus(elRef: RefObject<HTMLElement>): {
  isFullscreen: boolean;
  handleFullscreen: () => void;
} {
  function getBrowserFullscreenElementProp() {
    if (typeof document.fullscreenElement !== 'undefined') {
      return 'fullscreenElement';
    }
    if (typeof document.mozFullScreenElement !== 'undefined') {
      return 'mozFullScreenElement';
    }
    if (typeof document.msFullscreenElement !== 'undefined') {
      return 'msFullscreenElement';
    }
    if (typeof document.webkitFullscreenElement !== 'undefined') {
      return 'webkitFullscreenElement';
    }
    throw new Error('fullscreenElement is not supported by this browser');
  }

  const [isFullscreen, setIsFullscreen] = useState(document[getBrowserFullscreenElementProp()] !== null);

  const setFullscreen = () => {
    if (typeof document.webkitFullscreenElement !== 'undefined') {
      if (elRef.current && elRef.current.webkitRequestFullscreen) {
        elRef.current.webkitRequestFullscreen();
      }
    } else {
      const promise = new Promise((resolve) => {
        if (elRef.current && elRef.current.requestFullscreen) resolve(elRef.current.requestFullscreen());
      });
      promise.then(() => {
        setIsFullscreen(document[getBrowserFullscreenElementProp()] !== null);
      });
    }
  };

  const handleFullscreen = () => {
    if (isFullscreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
      if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    } else {
      setFullscreen();
    }
  };

  useLayoutEffect(() => {
    document.onwebkitfullscreenchange = () => {
      setIsFullscreen(document[getBrowserFullscreenElementProp()] !== null);
    };
    document.onfullscreenchange = () => {
      setIsFullscreen(document[getBrowserFullscreenElementProp()] !== null);
    };

    return () => {
      document.onfullscreenchange = null;
    };
  });

  return { isFullscreen, handleFullscreen };
}
